import React, { useState, useEffect, useContext } from "react";
import { FaEdit, FaTrash, FaFutbol } from "react-icons/fa";
import { BalanceContext } from "../context/BalanceContext";
import { ProjectsContext  } from "../context/ProjectsContext";
import { UserContext } from "../context/UserContext";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import axios from "axios";
import {
  FaTicketAlt,
  FaChartBar,
  FaCalendar,
  FaPlus,
  FaCheck,
  FaTimes,
  FaClock,
  FaBan,
  FaChevronRight,
  FaCoins,
  FaPercentage,
} from "react-icons/fa";
import MatchesList from "../components/MatchesList";
import { AnimatePresence, motion } from "framer-motion";

/*
|--------------------------------------------------------------------------
| AUTH / API HELPERS
|--------------------------------------------------------------------------
*/

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const Calendar = () => {
  const [betsByDate, setBetsByDate] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [newBetTitle, setNewBetTitle] = useState("");
  const { balance, setBalance } = useContext(BalanceContext);
  const { projects, isLoading: projectsLoading, error: projectsError } = useContext(ProjectsContext);
  const [dailyTicket, setDailyTicket] = useState(null);
  const [loadingDailyTicket, setLoadingDailyTicket] = useState(false);
  const [monthlySnapshot, setMonthlySnapshot] = useState(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  
  const [fetchProjectsLoading, setFetchProjectsLoading] = useState(true);
  const [fetchedProjects, setFetchedProjects] = useState([]);
  const [fetchedTickets, setFetchedTickets] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // Funcție pentru verificarea utilizatorului VisionRoyale
  const isVisionRoyale = () => {
    if (!user || !user.username) return false;
    return user.username === "VisionRoyale";
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    return `${year}-${month}-${day}`;
  };

  const isFutureDate = (dateString) => {
    if (!dateString) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [year, month, day] = dateString.split("-").map(Number);
    const selectedDateObj = new Date(year, month - 1, day);
    selectedDateObj.setHours(0, 0, 0, 0);

    return selectedDateObj >= today - 1 * 24 * 60 * 60 * 1000;
  };

  const determineMatchOutcome = (match) => {
    if (match.outcome && match.outcome !== "pending") return match.outcome;
    if (match.prediction_outcome && match.prediction_outcome !== "pending")
      return match.prediction_outcome;
    if (match.result && match.result !== "pending") return match.result;

    const isMatchFinished =
      match.match_status &&
      ["FT", "AET", "PEN", "FT_PEN"].includes(match.match_status);

    if (isMatchFinished) {
      return "pending";
    }

    return "pending";
  };

  const getCardBorder = (matchOutcome, matchStatus) => {
    if (matchOutcome === "won") return "border-green-500 border-2";
    if (matchOutcome === "lost") return "border-red-500 border-2";
    if (matchOutcome === "void") return "border-gray-500 border";
    if (matchStatus === "published") return "border-blue-400 border-2";
    return "border-gray-700 border";
  };

  const getTicketHeaderBorder = (ticket) => {
    if (!ticket || !ticket.matches || ticket.matches.length === 0) {
      return "border-gray-700 border";
    }

    const anyMatchLost = ticket.matches.some((m) => {
      const outcome = m.outcome || m.result || m.prediction_outcome;
      return outcome === "lost";
    });

    if (anyMatchLost) {
      return "border-red-500 border-2";
    }

    const allMatchesWon = ticket.matches.every((m) => {
      const outcome = m.outcome || m.result || m.prediction_outcome;
      return outcome === "won";
    });

    if (allMatchesWon) {
      return "border-green-500 border-2";
    }

    return "border-blue-400 border-2";
  };

  const getTicketDisplayStatus = (ticket) => {
    if (!ticket || !ticket.matches || ticket.matches.length === 0) {
      return ticket?.status || "draft";
    }

    const anyMatchLost = ticket.matches.some((m) => {
      const outcome = m.outcome || m.result || m.prediction_outcome;
      return outcome === "lost";
    });

    if (anyMatchLost) {
      return "lost";
    }

    const allMatchesWon = ticket.matches.every((m) => {
      const outcome = m.outcome || m.result || m.prediction_outcome;
      return outcome === "won";
    });

    if (allMatchesWon) {
      return "won";
    }

    return "pending";
  };

  const calculateMonthlyTicketStatus = (ticket) => {
    if (!ticket || !ticket.matches || ticket.matches.length === 0) {
      return "pending";
    }

    let hasLost = false;
    let hasPending = false;
    let allWon = true;

    for (const match of ticket.matches) {
      const outcome = (
        match.outcome ||
        match.result ||
        match.prediction_outcome ||
        "pending"
      ).toLowerCase();

      if (outcome === "lost") {
        hasLost = true;
        allWon = false;
        break;
      }

      if (
        outcome === "pending" ||
        outcome === "null" ||
        outcome === "undefined"
      ) {
        hasPending = true;
        allWon = false;
      }

      if (outcome !== "won") {
        allWon = false;
      }
    }

    if (hasLost) {
      return "lost";
    }

    if (hasPending) {
      return "pending";
    }

    if (allWon) {
      return "won";
    }

    return "pending";
  };

  const getStatusInfo = (status) => {
      switch (status) {
        case "won":
          return {
            label: "Won",
            className:
              "bg-green-900/40 text-green-400 border-green-500/30",
            icon: <FaCheck />,
          };
  
        case "lost":
          return {
            label: "Lost",
            className:
              "bg-red-900/40 text-red-400 border-red-500/30",
            icon: <FaTimes />,
          };
  
        case "cancelled":
          return {
            label: "Cancelled",
            className:
              "bg-gray-700/60 text-gray-400 border-gray-600/30",
            icon: <FaBan />,
          };
  
        default:
          return {
            label: "Pending",
            className:
              "bg-blue-900/40 text-blue-400 border-blue-500/30",
            icon: <FaClock />,
          };
      }
    };
  
    const formatDate = (date) => {
      if (!date) return "-";
  
      try {
        return new Date(date).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch {
        return "-";
      }
    };
  
    const formatMoney = (value) => {
      if (value === null || value === undefined) return "-";
  
      return Number(value).toFixed(2);
    };

  useEffect(() => {
    const todayFormatted = getTodayFormatted();
    setSelectedDate(todayFormatted);

    fetchProjects();
    fetchTicketsByDate(todayFormatted);

    const storedBets = JSON.parse(localStorage.getItem("betsByDate"));
    if (storedBets) {
      setBetsByDate(storedBets);
    }
  }, []);


  useEffect(() => {
    if (selectedDate) {
      fetchDailyTicketForDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (Object.keys(betsByDate).length > 0) {
      localStorage.setItem("betsByDate", JSON.stringify(betsByDate));
    }
  }, [betsByDate]);

  useEffect(() => {
    localStorage.setItem("projects", JSON.stringify(projects));
  }, [projects]);

  const fetchProjects = async () => {
    setFetchProjectsLoading(true);

    try {
        const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/projects/getallowed`,
        {
            headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
            },
        },
        );

        setFetchedProjects(response.data.projects || []);
    } catch (error) {
        console.error("Error fetching projects:", error);
        setFetchedProjects([]);
    } finally {
        setFetchProjectsLoading(false);
    }
  };

  const fetchMonthlySnapshot = async (year, month) => {
    setLoadingSnapshot(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No token found, skipping Monthly Snapshot fetch");
        setMonthlySnapshot(null);
        setLoadingSnapshot(false);
        return;
      }

      console.log(`Fetching monthly snapshot for ${year}-${month}`);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-snapshot/${year}/${month}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.snapshot) {
        const snapshotData = response.data.snapshot;

        const monthlyStats = {
          totalTickets: 0,
          wonTickets: 0,
          lostTickets: 0,
          pendingTickets: 0,
          roi: 0,
          totalStake: 0,
          totalWinnings: 0,
          tickets: [],
        };

        if (snapshotData.tickets && Array.isArray(snapshotData.tickets)) {
          monthlyStats.tickets = snapshotData.tickets;
          monthlyStats.totalTickets = snapshotData.tickets.length;

          snapshotData.tickets.forEach((ticket) => {
            const ticketStatus = calculateMonthlyTicketStatus(ticket);

            if (ticketStatus === "won") {
              monthlyStats.wonTickets++;
              monthlyStats.totalStake += parseFloat(ticket.stake) || 0;
              monthlyStats.totalWinnings +=
                parseFloat(ticket.potential_win) || 0;
            } else if (ticketStatus === "lost") {
              monthlyStats.lostTickets++;
              monthlyStats.totalStake += parseFloat(ticket.stake) || 0;
            } else {
              monthlyStats.pendingTickets++;
            }
          });

          const totalCompleted =
            monthlyStats.wonTickets + monthlyStats.lostTickets;
          if (totalCompleted > 0 && monthlyStats.totalStake > 0) {
            const profit = monthlyStats.totalWinnings - monthlyStats.totalStake;
            monthlyStats.roi = (profit / monthlyStats.totalStake) * 100;
          }
        } else {
          monthlyStats.totalTickets = snapshotData.totalTickets || 0;
          monthlyStats.wonTickets = snapshotData.wonTickets || 0;
          monthlyStats.lostTickets = snapshotData.lostTickets || 0;
          monthlyStats.roi = snapshotData.roi || 0;
        }

        console.log("Monthly Snapshot loaded:", monthlyStats);
        setMonthlySnapshot(monthlyStats);
      } else {
        console.log("No Monthly Snapshot available");
        setMonthlySnapshot(null);
      }
    } catch (error) {
      console.error("Error fetching Monthly Snapshot:", error);
      setMonthlySnapshot(null);
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const fetchTicketsByDate = async (date) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/tickets/date/${date}`,
        {
          headers: getAuthHeaders(),
          timeout: 10000,
        }
      );

      console.log('req: ', `${process.env.REACT_APP_BACKEND_URL}/tickets/date/${date}`);

      if(response.data) {
        setFetchedTickets(response.data.tickets);
      }
    } catch (error) {
      //
    }
  }

  const handleNewTicket = () => {
    if (!selectedProject) {
        alert("Please select a project first.");
        return;
    }

    navigate("/tickets/new", {
        state: {
        project: selectedProject,
        },
    });
  };

  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    fetchMonthlySnapshot(year, month);
  }, [currentDate]);

  const fetchDailyTicketForDate = async (date) => {
    setLoadingDailyTicket(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No token found, skipping Daily Value Snapshot fetch");
        setDailyTicket(null);
        setLoadingDailyTicket(false);
        return;
      }

      const [year, month, day] = date.split("-").map(Number);
      const selectedDateObj = new Date(year, month - 1, day);
      const today = new Date();

      const selectedUTC = Date.UTC(
        selectedDateObj.getFullYear(),
        selectedDateObj.getMonth(),
        selectedDateObj.getDate(),
      );

      const todayUTC = Date.UTC(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );

      const diffTime = selectedUTC - todayUTC;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      console.log(`Fetching ticket for date: ${date}, dayOffset: ${diffDays}`);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/daily-ticket/${diffDays}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.ticket) {
        console.log("Daily Value Snapshot FULL RESPONSE:", response.data);
        console.log("Daily Value Snapshot TICKET:", response.data.ticket);

        if (
          response.data.ticket.matches &&
          response.data.ticket.matches.length > 0
        ) {
          console.log("First match data:", response.data.ticket.matches[0]);
          console.log(
            "First match keys:",
            Object.keys(response.data.ticket.matches[0]),
          );
        }

        setDailyTicket(response.data.ticket);
      } else {
        console.log("No Daily Value Snapshot available");
        setDailyTicket(null);
      }
    } catch (error) {
      console.error("Error fetching Daily Value Snapshot:", error);
      setDailyTicket(null);
    } finally {
      setLoadingDailyTicket(false);
    }
  };

  const generateDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    let days = [];
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const adjustedFirstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;

    for (let i = 0; i < adjustedFirstDayOfWeek - 1; i++) {
      days.push(null);
    }

    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const formattedDate = `${year}-${month + 1}-${day}`;
      days.push(formattedDate);
    }

    return days;
  };

  const handleReadyClick = (betId) => {
    if (isVisionRoyale()) {
      alert("This action is disabled for Test User VisionRoyale.");
      return;
    }

    setBetsByDate((prev) => {
      const updatedBets = { ...prev };
      const betList = updatedBets[selectedDate] || [];
      const betToUpdate = betList.find((bet) => bet.id === betId);

      if (!betToUpdate) {
        console.warn("Pariul nu a fost găsit!");
        return prev;
      }

      if (parseFloat(betToUpdate.stake) === 0) {
        alert("Stake must not be 0!");
        return prev;
      }

      if (betToUpdate.bettingMarkets.length === 0) {
        alert("Market must not be empty!");
        return prev;
      }

      const hasPendingMarkets = betToUpdate.bettingMarkets.some(
        (market) => market.status === "PENDING",
      );
      if (hasPendingMarkets) {
        alert("Event cannot be closed because status is pending");
        return prev;
      }

      const updatedStatus = calculateBetStatus(betToUpdate.bettingMarkets);
      const totalOdds = betToUpdate.bettingMarkets.reduce((acc, market) => {
        const odds = parseFloat(market.odds);
        return acc * odds;
      }, 1);

      const stake = parseFloat(betToUpdate.stake) || 0;
      const winnings =
        updatedStatus === "LOST" || updatedStatus === "PENDING"
          ? 0
          : updatedStatus === "WON"
            ? stake * totalOdds
            : 0;

      if (updatedStatus === "WON" && !betToUpdate.isReady) {
        setBalance((prevBalance) => prevBalance + winnings);
      }

      betToUpdate.isReady = true;
      betToUpdate.updatedStatus = updatedStatus;
      betToUpdate.totalOdds = totalOdds;
      betToUpdate.winnings = winnings;

      return updatedBets;
    });
  };

  const getProfitLossColor = (profitLoss) => {
    if (profitLoss > 0) return "text-green-500";
    if (profitLoss < 0) return "text-red-500";
    return "text-gray-400";
  };

  const getProfitLossLabel = (profitLoss) => {
    if (profitLoss > 0) return "Today's Profit";
    if (profitLoss < 0) return "Today's Loss";
    return "Today's Result";
  };

  const calculateDailyProfitLoss = (date) => {
    const bets = betsByDate[date] || [];
    let totalProfitLoss = 0;

    bets.forEach((bet) => {
      if (bet.isReady) {
        if (bet.updatedStatus === "WON") {
          totalProfitLoss += bet.winnings - bet.stake;
        } else if (bet.updatedStatus === "LOST") {
          totalProfitLoss -= bet.stake;
        }
      }
    });

    return totalProfitLoss;
  };

  const goToPreviousMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const calculateBetStatus = (bettingMarkets) => {
    if (!bettingMarkets.length) {
      return "PENDING";
    }
    if (bettingMarkets.some((market) => market.status === "LOST")) {
      return "LOST";
    }
    if (bettingMarkets.every((market) => market.status === "WON")) {
      return "WON";
    }
    if (bettingMarkets.some((market) => market.status === "PENDING")) {
      return "PENDING";
    }
    return "unknown";
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const calculateMargin = (odd, winProbability) => {
    const oddNum = typeof odd === "string" ? parseFloat(odd) : odd || 0;
    const winProbNum =
      typeof winProbability === "string"
        ? parseFloat(winProbability)
        : winProbability || 0;

    if (!oddNum || !winProbNum) return 0;

    const impliedProbability = 100 / oddNum;
    return winProbNum - impliedProbability;
  };

  const getValueScoreBarColor = (margin) => {
    if (margin >= 15) return "bg-green-500";
    if (margin >= 10) return "bg-green-400";
    if (margin >= 5) return "bg-yellow-400";
    return "bg-yellow-300";
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "bg-gray-500", text: "Draft" },
      published: { color: "bg-blue-500", text: "Published" },
      won: { color: "bg-green-600", text: "WON" },
      lost: { color: "bg-red-500", text: "LOST" },
      void: { color: "bg-gray-600", text: "VOID" },
    };
    const config = statusConfig[status] || {
      color: "bg-gray-400",
      text: status,
    };
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const TicketMatchCard = ({ match, index }) => {
    const margin = calculateMargin(match.odd, match.win_probability);
    const displayValueScore = Math.abs(margin);
    const matchOutcome = determineMatchOutcome(match);
    const cardBorder = getCardBorder(matchOutcome, match.status);
    const finalHomeGoals = match.goals_home;
    const finalAwayGoals = match.goals_away;
    const hasFinalResult = finalHomeGoals !== null && finalAwayGoals !== null;
    const { user: currentUser } = useContext(UserContext);
    const userTier = currentUser?.subscription?.tier || "trial";

    const isMatchFinished =
      match.match_status &&
      ["FT", "AET", "PEN", "FT_PEN", "CANCL", "POSTP", "ABAN"].includes(
        match.match_status,
      );

    const shouldBlur = () => {
      if (userTier === "pro" || userTier === "premium") {
        return false;
      }
      if (selectedDate && isFutureDate(selectedDate)) {
        return index >= 1;
      }
      return false;
    };

    const blurMatch = shouldBlur();
    const isFuture = selectedDate && isFutureDate(selectedDate);

    const formatTime = (dateString) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return "";
      }
    };

    return (
      <div
        className={`rounded-lg border p-3 mb-2 hover:shadow-md transition-all bg-gray-700 ${cardBorder} ${
          blurMatch ? "relative" : ""
        }`}
      >
        {blurMatch && (
          <div className="absolute inset-0 bg-black/50 rounded-lg z-10" />
        )}

        <div className={blurMatch ? "blur-sm" : ""}>
          <div className="grid grid-cols-[max-content_minmax(0,1fr)_max-content] items-center mb-3 text-green-400 w-full overflow-hidden">
            <div className="text-left">
              <div className="text-xs font-semibold truncate pr-1">
                {match.country || match.country_name}
              </div>
            </div>

            <div className="text-center">
              <div className="text-sm font-bold px-2 break-words">
                {match.league || match.league_name || "Unknown League"}
              </div>
            </div>

            <div className="text-right flex items-center justify-end gap-1">
              <span>⏰</span>
              <span className="text-xs">
                {formatTime(match.match_date) ||
                  formatTime(match.match_datetime) ||
                  ""}
              </span>
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 pr-2">
                <div className="text-sm font-semibold text-purple-300 break-words text-right">
                  {match.home_team}
                </div>
              </div>

              <div className="flex-shrink-0 w-12 sm:w-16 mx-1 flex flex-col items-center">
                {isMatchFinished ? (
                  hasFinalResult ? (
                    <>
                      <div className="text-sm sm:text-base font-bold text-green-400 whitespace-nowrap">
                        {finalHomeGoals} - {finalAwayGoals}
                      </div>
                      <div className="text-[10px] text-gray-500">FT</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] text-gray-500">FT</div>
                    </>
                  )
                ) : (
                  <div className="px-2 py-1 bg-gray-600 rounded">
                    <span className="text-xs font-bold text-purple-400">
                      VS
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0 pl-2">
                <div className="text-sm font-semibold text-purple-300 break-words text-left">
                  {match.away_team}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-center mb-1">
              <div className="text-sm font-semibold text-green-300">
                {match.market_type}
              </div>
              <div className="text-sm font-bold text-purple-400 mt-1">
                {match.selection}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
              <div className="text-xs text-gray-400">Market Odds</div>
              <div className="text-sm font-bold text-purple-400">
                {match.odd}
              </div>
            </div>
            <div className="bg-gray-800/60 p-2 rounded text-center border border-gray-700/60">
              <div className="text-xs text-gray-400">Model Probability</div>
              <div className="text-sm font-bold text-purple-400">
                {match.win_probability}%
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="font-semibold text-gray-400">VALUE EDGE</span>
              <span className="font-bold text-green-400">
                {displayValueScore.toFixed(1)}
              </span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`${getValueScoreBarColor(
                  displayValueScore,
                )} h-full transition-all duration-300`}
                style={{
                  width: `${Math.min(100, displayValueScore * 4)}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUpgradeButton = () => {
    if (
      !dailyTicket ||
      !dailyTicket.matches ||
      dailyTicket.matches.length === 0
    ) {
      return null;
    }

    const userTier = user?.subscription?.tier || "trial";

    if (userTier === "pro" || userTier === "premium") {
      return null;
    }

    if (!selectedDate || !isFutureDate(selectedDate)) {
      return null;
    }

    const hasMultipleMatches = dailyTicket.matches.length > 1;

    if (!hasMultipleMatches) {
      return null;
    }

    return (
      <div className="mt-6 pt-6 border-t border-gray-700">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-600 rounded-lg p-4 text-center">
          <h3 className="text-lg font-bold text-green-400 mb-2">
            Upgrade to See All Matches
          </h3>
          <p className="text-gray-300 mb-4">
            {userTier === "trial"
              ? "Trial users can only see one match. Upgrade to Pro or Premium plan to see all matches."
              : "Basic plan users can only see one match. Upgrade to Pro or Premium plan to see all matches."}
          </p>
          <button
            onClick={() => navigate("/subscription-plans")}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            View Subscription Plans
          </button>
        </div>
      </div>
    );
  };

  const visionRoyale = isVisionRoyale();

  return (
    <Layout>
      <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center mb-4 sm:mb-6 text-green-400">
        Market Activity Calendar
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-gray-800 p-5 rounded-xl shadow-lg">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-green-400 text-center flex items-center justify-center gap-2">
            <FaTicketAlt />{" "}
            {currentDate.toLocaleString("en-US", { month: "long" })} Performance
            Snapshot
          </h2>
          {/* Monthly Statistics Wrapper Card */}
          {monthlySnapshot && !loadingSnapshot && (
            <div
              className="mb-4 bg-gradient-to-br from-gray-800 to-gray-900 p-4 rounded-lg border-2 shadow-lg"
              style={{
                borderColor:
                  monthlySnapshot.roi > 0
                    ? "#10b981"
                    : monthlySnapshot.roi < 0
                      ? "#ef4444"
                      : "#6b7280",
              }}
            >
              <div className="grid grid-cols-2 gap-2">
                {/* Total Tickets Card */}
                <div className="bg-gray-900 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">Total Snapshots</div>
                  <div className="text-xl font-bold text-blue-400">
                    {monthlySnapshot.totalTickets}
                  </div>
                </div>

                {/* Won Card */}
                <div className="bg-gray-900 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">Won</div>
                  <div className="text-xl font-bold text-green-400">
                    {monthlySnapshot.wonTickets}
                  </div>
                </div>

                {/* Lost Card */}
                <div className="bg-gray-900 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">Lost</div>
                  <div className="text-xl font-bold text-red-400">
                    {monthlySnapshot.lostTickets}
                  </div>
                </div>

                {/* ROI Card - Neutral styling */}
                <div className="bg-gray-900 p-2 rounded border border-gray-700 text-center">
                  <div className="text-xs text-gray-400">ROI</div>
                  <div
                    className={`text-xl font-bold ${
                      monthlySnapshot.roi > 0
                        ? "text-green-400"
                        : monthlySnapshot.roi < 0
                          ? "text-red-400"
                          : "text-gray-300"
                    }`}
                  >
                    {monthlySnapshot.roi > 0 ? "+" : ""}
                    {monthlySnapshot.roi.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}

          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-green-400 text-center flex items-center justify-center gap-2">
            <FaTicketAlt /> Daily Value Snapshot
          </h2>

          {loadingDailyTicket ? (
            <div className="flex items-center justify-center h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500 mx-auto mb-2"></div>
                <p className="text-gray-400">Loading Daily Value Snapshot...</p>
              </div>
            </div>
          ) : dailyTicket ? (
            dailyTicket.status && dailyTicket.status !== "draft" ? (
              <div className="space-y-4">
                {/* Ticket Header cu background original și bordură colorată */}
                <div
                  className={`bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg p-4 ${getTicketHeaderBorder(
                    dailyTicket,
                  )}`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Market Overview
                      </h3>
                      <p className="text-xs text-gray-300">
                        {selectedDate === getTodayFormatted()
                          ? "Today"
                          : selectedDate}
                      </p>
                    </div>
                    {getStatusBadge(getTicketDisplayStatus(dailyTicket))}
                  </div>

                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-900 p-3 rounded-lg flex flex-col items-center justify-center">
                        <div className="flex items-center gap-2 mb-1">
                          <FaChartBar className="text-green-400 text-sm" />
                          <div className="text-xs text-gray-400">
                            Combined Market Odds
                          </div>
                        </div>
                        <div
                          className={`text-xl font-bold ${
                            dailyTicket.total_odd >= 4 &&
                            dailyTicket.total_odd <= 6
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          {parseFloat(dailyTicket.total_odd).toFixed(2)}
                        </div>
                      </div>

                      <div className="bg-gray-900 p-3 rounded-lg flex flex-col items-center justify-center">
                        <div className="flex items-center gap-2 mb-1">
                          <FaPercentage className="text-blue-400 text-sm" />
                          <div className="text-xs text-gray-400">
                            Model Probability
                          </div>
                        </div>
                        <div className="text-xl font-bold text-blue-400">
                          {parseFloat(dailyTicket.total_probability).toFixed(1)}
                          %
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-900 p-3 rounded-lg flex flex-col items-center justify-center">
                      <div className="flex items-center gap-2 mb-1">
                        <FaPercentage className="text-sm text-yellow-400" />
                        <div className="text-xs text-gray-400">ROI</div>
                      </div>
                      <div className="text-lg font-bold text-yellow-400">
                        {(
                          ((parseFloat(dailyTicket.potential_win) -
                            parseFloat(dailyTicket.stake)) /
                            parseFloat(dailyTicket.stake)) *
                          100
                        ).toFixed(1)}
                        %
                      </div>
                    </div>

                    <div className="bg-gray-900 p-3 rounded-lg flex flex-col items-center justify-center">
                      <div className="flex items-center gap-2 mb-1">
                        <FaCalendar className="text-purple-400 text-sm" />
                        <div className="text-xs text-gray-400">
                          Number of Events
                        </div>
                      </div>
                      <div className="text-lg font-bold text-purple-400">
                        {dailyTicket.match_count || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {dailyTicket.matches && dailyTicket.matches.length > 0 && (
                  <div className="bg-gray-900 border-2 border-gray-500 rounded-lg p-4">
                    <h4 className="font-bold text-gray-300 mb-3 flex items-center gap-2">
                      <FaFutbol /> Included Events ({dailyTicket.matches.length}
                      )
                    </h4>
                    <div className="space-y-2">
                      {dailyTicket.matches.map((match, index) => (
                        <Link 
                          key={match.id || index} 
                          to={`${process.env.REACT_APP_FRONTEND_URL}/match/${match.match_id}`} 
                          className="block"
                        >
                          <TicketMatchCard
                            key={match.id || index}
                            match={match}
                            index={index}
                          />
                        </Link>
                      ))}
                    </div>

                    {renderUpgradeButton()}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[400px]">
                <div className="text-center">
                  <FaTicketAlt className="text-4xl text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">
                    {dailyTicket && dailyTicket.status === "draft"
                      ? "Daily Value Snapshot is in draft"
                      : "Daily Value Snapshot not available"}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {dailyTicket && dailyTicket.status === "draft"
                      ? "Ticket will be available when published"
                      : "No ticket available for this date"}
                  </p>
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-[400px]">
              <div className="text-center">
                <FaTicketAlt className="text-4xl text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">
                  No Daily Value Snapshot available
                </p>
                <p className="text-gray-600 text-sm">
                  {selectedDate === getTodayFormatted()
                    ? "Check back later for today's ticket"
                    : "No ticket published for this date"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Container pentru coloana dreapta (calendar + betting markets) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cardul Calendar - sus în coloana dreapta */}
          <div className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <button
                className="text-xl sm:text-2xl text-green-400 hover:text-blue-500 transition duration-200"
                onClick={goToPreviousMonth}
              >
                ←
              </button>
              <span className="text-lg sm:text-xl md:text-2xl font-medium text-green-400">
                {currentDate.toLocaleString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                className="text-xl sm:text-2xl text-green-400 hover:text-blue-500 transition duration-200"
                onClick={goToNextMonth}
              >
                →
              </button>
            </div>

            {/* Zilele săptămânii */}
            <div className="grid grid-cols-7 gap-2 mb-3">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center font-medium text-green-400 text-sm sm:text-base py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Zilele lunii - cu selecția zilei de azi */}
            <div className="grid grid-cols-7 gap-2">
              {generateDays().map((date, index) => {
                const profitLoss = date ? calculateDailyProfitLoss(date) : 0;

                const bgColor =
                  profitLoss > 0
                    ? "bg-green-500"
                    : profitLoss < 0
                      ? "bg-red-500"
                      : "bg-gray-700";

                const isToday = date === getTodayFormatted();
                const isSelected = date === selectedDate;

                return (
                  <div
                    key={index}
                    onClick={() => {
                      if (date) {
                        setSelectedDate(date);
                        fetchTicketsByDate(date);
                        console.log('clicked on ' + date);
                      }
                    }}
                    className={`relative cursor-pointer p-2 sm:p-3 rounded-lg text-center text-sm sm:text-base min-h-[60px] flex flex-col justify-center items-center ${
                      date ? bgColor : "bg-transparent"
                    } ${isSelected ? "ring-2 ring-green-400" : ""} ${
                      isToday && !isSelected ? "ring-2 ring-yellow-400" : ""
                    }`}
                    title={isToday ? "Today" : date}
                  >

                    {/* Day number */}
                    {date ? date.split("-")[2] : ""}

                    {isToday && (
                      <div className="mt-1 text-xs text-yellow-300 font-bold">
                        TODAY
                      </div>
                    )}

                    {date && !isToday && betsByDate[date]?.length > 0 && (
                      <div className="mt-1 text-xs text-gray-300">
                        Events {betsByDate[date].length}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>


            {/* Info pentru ziua selectată - doar profit/loss */}
            {selectedDate && calculateDailyProfitLoss(selectedDate) !== 0 && (
              <div className="mt-6 pt-4 border-t border-gray-700">
                <div
                  className={`text-base sm:text-lg font-medium text-right ${getProfitLossColor(
                    calculateDailyProfitLoss(selectedDate),
                  )}`}
                >
                  {getProfitLossLabel(calculateDailyProfitLoss(selectedDate))}:{" "}
                  {calculateDailyProfitLoss(selectedDate).toFixed(2)}
                </div>
              </div>
            )}
          </div>

          {/* Loading state for snapshot */}
          {loadingSnapshot && (
            <div className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700">
              <div className="flex items-center justify-center h-[200px]">
                <div className="text-center">
                  <div className="animate-spin mb-3">
                    <FaChartBar className="text-4xl text-gray-600 mx-auto" />
                  </div>
                  <p className="text-gray-500">Loading monthly statistics...</p>
                </div>
              </div>
            </div>
          )}

          {/* TEST Calendar */}
          {selectedDate && (
            <div className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700">
              <p className="py-2 mb-2">Date selected: {selectedDate}</p>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                {/* Project selector */}
                <select
                value={selectedProject?.id || ""}
                onChange={(e) => {
                    const project = fetchedProjects.find(
                    (p) => String(p.id) === String(e.target.value)
                    );

                    setSelectedProject(project || null);
                }}
                disabled={fetchProjectsLoading}
                className="min-w-[220px] px-4 py-2.5 rounded-lg bg-gray-700/80 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                <option value="">
                    {fetchProjectsLoading ? "Loading projects..." : "Select project"}
                </option>

                {fetchedProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                    {project.name}
                    </option>
                ))}
                </select>

                <button
                onClick={handleNewTicket}
                disabled={!selectedProject}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    selectedProject
                    ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
                >
                <FaPlus className="inline mr-2" />
                New Ticket
                </button>
              </div>
              {fetchedTickets.map((ticket) => {
                const status = getStatusInfo(ticket.status);

                return (
                  <tr
                    key={ticket.id}
                    onClick={() =>
                      navigate(`/tickets/${ticket.id}`)
                    }
                    className="border-t border-green-500/10 hover:bg-gray-700/30 transition-colors cursor-pointer"
                  >

                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-green-900/40 flex items-center justify-center">
                          <FaTicketAlt className="text-green-400" />
                        </div>

                        <div>
                          <p className="text-white font-medium">
                            Ticket #
                            {ticket.ticket_number ||
                              ticket.id}
                          </p>

                          <p className="text-xs text-gray-500">
                            ID: {ticket.id}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <span className="text-gray-300">
                        {ticket.project_name ||
                          "No project"}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-center">
                      <span className="text-white font-medium">
                        {formatMoney(ticket.stake)}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-center">
                      <span className="text-green-400 font-semibold">
                        {Number(ticket.total_odd || 0).toFixed(2)}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-center">
                      <span className="text-white font-medium">
                        {formatMoney(
                          ticket.potential_return,
                        )}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${status.className}`}
                      >
                        {status.icon}
                        {status.label}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-center text-sm text-gray-400">
                      {formatDate(ticket.created_at)}
                    </td>

                    <td className="px-4 py-4 text-right">
                      <FaChevronRight className="text-gray-500" />
                    </td>

                  </tr>
                );
              })}
            </div>
          )}         
        </div>
      </div>
    </Layout>
  );
};

export default Calendar;
